/**
 * RandomizedConflictModal — the "this lead is already enrolled somewhere"
 * confirmation (multi-study lead lists, plan §6.4).
 *
 * Owner request 2026-08-14: "once a lead is randomized into a study there
 * should be a flag that pops up if they try to enroll in a new study while
 * still randomized."
 *
 * The backend blocks the action with a 409 { reason: 'randomized_conflict' }.
 * This modal is the acknowledgement: confirming re-sends the SAME request with
 * randomized_conflict_acknowledged:true, which proceeds and writes a timeline
 * event + audit row. Cancelling changes nothing.
 *
 * Deliberately not a hard stop — recruiters do legitimately line a randomized
 * subject up for a follow-on study — so the confirm is destructive-styled
 * rather than disabled. Same file layout, Z layering and Escape handling as
 * StageStudyConfirmModal.
 */
import { useEffect } from 'react';
import { AlertTriangle } from 'lucide-react';
import { relTime } from '../lib';
import { Z } from './ui';

interface Props {
  /** The study they are being considered for (may be unknown from a bulk move). */
  targetStudyTitle?: string | null;
  /** The study they are currently randomized in — from the 409 body. */
  randomizedStudyTitle?: string | null;
  /** When they were randomized — from the 409 body. */
  randomizedAt?: string | null;
  /** Set for the bulk path: how many leads the acknowledgement covers. */
  bulkCount?: number;
  onConfirm: () => void;
  onCancel: () => void;
}

export default function RandomizedConflictModal({
  targetStudyTitle, randomizedStudyTitle, randomizedAt, bulkCount, onConfirm, onCancel,
}: Props) {
  const bulk = typeof bulkCount === 'number' && bulkCount > 0;
  const enrolledIn = randomizedStudyTitle || 'another study';

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <div
      className={`fixed inset-0 ${Z.modal} flex items-center justify-center bg-black/40 p-4`}
      onClick={onCancel}
      role="presentation"
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl dark:bg-gray-800"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`Lead is randomized in ${enrolledIn}`}
      >
        <h2 className="flex items-start gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
          <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-500" />
          <span>
            {bulk ? 'Some of these leads are randomized' : `Lead is randomized in ${enrolledIn}`}
          </span>
        </h2>

        {bulk ? (
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            One or more of these {bulkCount} leads are currently randomized into a
            study. Continuing will consider them for
            {targetStudyTitle ? (
              <> <span className="font-medium text-gray-700 dark:text-gray-200">{targetStudyTitle}</span></>
            ) : ' another study'} while they are still enrolled.
          </p>
        ) : (
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            This lead was randomized into{' '}
            <span className="font-medium text-gray-700 dark:text-gray-200">{enrolledIn}</span>
            {randomizedAt ? ` ${relTime(randomizedAt)}` : ''} and has not completed or
            discontinued it. Continuing puts them in play for
            {targetStudyTitle ? (
              <> <span className="font-medium text-gray-700 dark:text-gray-200">{targetStudyTitle}</span></>
            ) : ' a second study'} while they are still enrolled.
          </p>
        )}

        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
          Their status on {enrolledIn} is unchanged either way. This is recorded
          on the lead’s timeline.
        </p>

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white transition-colors duration-150 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
          >
            Consider anyway
          </button>
        </div>
      </div>
    </div>
  );
}
